/**
 * Canvas comment pin hit-target geometry.
 *
 * The painted pin is a `PIN_SIZE_PX` square rotated -45deg about its
 * bottom-left tip. CSS hover scales the whole `.comment-pin` to
 * `CANVAS_PIN_HOVER_SCALE` from the same origin — the click target must
 * cover that hover-sized teardrop (plus the 2px ring), not just the inner
 * 25×25 layout box. Otherwise clicks on the colored ring fall through to
 * the canvas and create a new comment.
 */

export const CANVAS_PIN_SIZE_PX = 25;

/** Matches `.comment-pin:hover { transform: scale(1.125) }` in tldraw assets. */
export const CANVAS_PIN_HOVER_SCALE = 1.125;

/** Matches `box-shadow: 0 0 0 2px` on the pin body. */
export const CANVAS_PIN_RING_PX = 2;

export type PinPoint = { x: number; y: number };

/**
 * Teardrop polygon in shape-local coords where the pin TIP is at (0, baseSize).
 * Vertices are scaled to the hover size and expanded for the ring so tldraw
 * hit-testing and the HTML overlay agree.
 */
export function getCanvasPinHitPolygon(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): PinPoint[] {
  const hoverScale = options.hoverScale ?? CANVAS_PIN_HOVER_SCALE;
  const ringPx = options.ringPx ?? CANVAS_PIN_RING_PX;
  const s = baseSize;
  const k = Math.SQRT1_2;
  const tip = { x: 0, y: s };

  const diamond = [
    tip,
    { x: k * s, y: s - k * s },
    { x: 0, y: s - 2 * k * s },
    { x: -k * s, y: s - k * s },
  ];

  const scaled = diamond.map((p) => ({
    x: tip.x + hoverScale * (p.x - tip.x),
    y: tip.y + hoverScale * (p.y - tip.y),
  }));

  return scaled.map((p) => {
    const dx = p.x - tip.x;
    const dy = p.y - tip.y;
    const len = Math.hypot(dx, dy);
    if (len === 0) return p;
    return {
      x: p.x + (dx / len) * ringPx,
      y: p.y + (dy / len) * ringPx,
    };
  });
}

/** Axis-aligned bounds for the hover-sized hit target, tip at (0, baseSize). */
export function getCanvasPinHitBounds(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): { left: number; top: number; width: number; height: number } {
  const points = getCanvasPinHitPolygon(baseSize, options);
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return {
    left,
    top,
    width: Math.max(...xs) - left,
    height: Math.max(...ys) - top,
  };
}

/** Polygon vertices relative to {@link getCanvasPinHitBounds} top-left (for clip-path). */
export function getCanvasPinHitPolygonInBounds(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): PinPoint[] {
  const bounds = getCanvasPinHitBounds(baseSize, options);
  return getCanvasPinHitPolygon(baseSize, options).map((p) => ({
    x: p.x - bounds.left,
    y: p.y - bounds.top,
  }));
}

/**
 * Pin avatar shape layout: the painted body is `baseSize` with its tip at the
 * click point, but the teardrop + ring extend outside that box (negative left/top).
 * tldraw clips HTML to shape props — props must cover the full bounds and the
 * shape origin must shift so the tip stays on the click point.
 */
export function getCanvasPinAvatarLayout(
  baseSize: number = CANVAS_PIN_SIZE_PX,
): {
  bodySize: number;
  bounds: ReturnType<typeof getCanvasPinHitBounds>;
  /** Pin tip position inside the expanded shape-local frame. */
  tipLocalX: number;
  tipLocalY: number;
} {
  const bounds = getCanvasPinHitBounds(baseSize);
  return {
    bodySize: baseSize,
    bounds,
    tipLocalX: -bounds.left,
    tipLocalY: baseSize - bounds.top,
  };
}

/**
 * Expanded comment-avatar shape x/y so the tip stays at the user's click point.
 * `tipPageX/Y` is where the pin point should land (page space).
 */
export function getCanvasPinAvatarShapePosition(
  tipPageX: number,
  tipPageY: number,
  zoom: number,
  baseSize: number = CANVAS_PIN_SIZE_PX,
): { x: number; y: number; w: number; h: number } {
  const { bounds } = getCanvasPinAvatarLayout(baseSize);
  return {
    x: tipPageX + bounds.left / zoom,
    y: tipPageY - (baseSize - bounds.top) / zoom,
    w: bounds.width,
    h: bounds.height,
  };
}

/** Even-odd ray cast — shape-local coords, same space as getCanvasPinHitPolygon. */
export function isPointInCanvasPinHit(
  x: number,
  y: number,
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): boolean {
  const points = getCanvasPinHitPolygon(baseSize, options);
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;
    const intersects =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Point test in bounds-relative coords (same frame as clip-path / HTML overlay). */
export function isPointInCanvasPinHitBounds(
  x: number,
  y: number,
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): boolean {
  const bounds = getCanvasPinHitBounds(baseSize, options);
  return isPointInCanvasPinHit(
    x + bounds.left,
    y + bounds.top,
    baseSize,
    options,
  );
}

/**
 * visible pin instead of its axis-aligned bounding box.
 */
export function getCanvasPinHitClipPath(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): string {
  const bounds = getCanvasPinHitBounds(baseSize, options);
  const points = getCanvasPinHitPolygonInBounds(baseSize, options);
  const pct = points
    .map((p) => {
      const xPct = (p.x / bounds.width) * 100;
      const yPct = (p.y / bounds.height) * 100;
      return `${xPct.toFixed(2)}% ${yPct.toFixed(2)}%`;
    })
    .join(", ");
  return `polygon(${pct})`;
}

/**
 * Circular hit target fallback — centered on the round body, radius = hover-scaled
 * half-width. The pointy tip is intentionally excluded.
 */
export function getCanvasPinCircleHit(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): {
  cx: number;
  cy: number;
  radius: number;
  bounds: ReturnType<typeof getCanvasPinHitBounds>;
} {
  const hoverScale = options.hoverScale ?? CANVAS_PIN_HOVER_SCALE;
  const ringPx = options.ringPx ?? CANVAS_PIN_RING_PX;
  const s = baseSize;
  const k = Math.SQRT1_2;
  const radius = k * s * hoverScale * 0.85 + ringPx;
  // Round bulk sits above the tip; center near the diamond's vertical midline.
  const cx = 0;
  const cy = s - k * s * hoverScale;
  const bounds = {
    left: cx - radius,
    top: cy - radius,
    width: radius * 2,
    height: radius * 2,
  };
  return { cx, cy, radius, bounds };
}

export function getCanvasPinCircleHitClipPath(
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): string {
  const { cx, cy, radius, bounds } = getCanvasPinCircleHit(baseSize, options);
  const cxPct = ((cx - bounds.left) / bounds.width) * 100;
  const cyPct = ((cy - bounds.top) / bounds.height) * 100;
  const rPct = (radius / bounds.width) * 100;
  return `circle(${rPct.toFixed(2)}% at ${cxPct.toFixed(2)}% ${cyPct.toFixed(2)}%)`;
}

export function isPointInCanvasPinCircleHit(
  x: number,
  y: number,
  baseSize: number,
  options: { hoverScale?: number; ringPx?: number } = {},
): boolean {
  const { cx, cy, radius } = getCanvasPinCircleHit(baseSize, options);
  return (x - cx) ** 2 + (y - cy) ** 2 <= radius ** 2;
}
